import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Sparkles, Check, ChevronRight, X, Heart, CalendarPlus,
  Star, Shield, Zap, RotateCcw, ThumbsDown, MapPin, Clock,
  HelpCircle, Loader2, AlertCircle, ArrowUp, Trash2, Settings,
} from "lucide-react";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { toast } from "sonner";

const REJECTION_REASONS = [
  { id: "too_far", label: "Too far", icon: MapPin },
  { id: "not_my_thing", label: "Not my thing", icon: ThumbsDown },
  { id: "too_much_hassle", label: "Too much hassle", icon: Clock },
  { id: "been_there", label: "Been there", icon: RotateCcw },
];

function DealSelectionCard({ deal, selected, onToggle, disabled, dealStatus }) {
  const brandColor = deal.brand_colors?.[0] || "#3b82f6";
  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      onClick={() => !disabled && onToggle(deal.business_id)}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
        selected
          ? "border-sky-400 bg-sky-50/80 shadow-sm ring-1 ring-sky-200"
          : disabled
          ? "border-slate-100 bg-slate-50/50 opacity-50"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`decision-select-${deal.business_id}`}
    >
      <div className="relative">
        <BrandLogo name={deal.name} logoUrl={deal.logo_url} brandColor={brandColor} size="sm" />
        {selected && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-sky-500 rounded-full flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-bold text-slate-800 truncate">{deal.name}</p>
          {dealStatus && <UnifiedStatusBadge status={dealStatus} />}
        </div>
        <p className="text-[11px] text-slate-400 truncate">{deal.offer_title || deal.category}</p>
      </div>
      <div className="text-right flex-shrink-0">
        {deal.offer_value_est > 0 && <p className="text-sm font-bold text-emerald-600">${deal.offer_value_est}</p>}
        <div className="flex gap-0.5 justify-end mt-0.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className={`w-2.5 h-2.5 ${i < (deal.ease_stars || 0) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
          ))}
        </div>
      </div>
    </motion.button>
  );
}

function PreviousPickReminder({ reminder, onGoBack }) {
  if (!reminder) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-3"
      data-testid="previous-pick-reminder"
    >
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <AlertCircle className="w-4 h-4 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-amber-700">{reminder.reminder_text}</p>
          <p className="text-[11px] text-amber-600 mt-0.5">
            That one had: <span className="font-semibold">{reminder.what_was_good}</span>
          </p>
        </div>
        <button
          onClick={onGoBack}
          className="px-2.5 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center gap-1 flex-shrink-0 hover:bg-amber-200 transition"
          data-testid="go-back-to-previous"
        >
          <ArrowUp className="w-3 h-3" />Go back
        </button>
      </div>
    </motion.div>
  );
}

function AIRecommendationCard({ deal, onAccept, onReject, showReasons, onReasonSelect, pickNumber, totalPicks, previousReminder, onGoBackToPrevious }) {
  const brandColor = deal.brand_colors?.[0] || "#6366f1";
  const comparativeReasons = deal.comparative_reasons || [];
  const genericReasons = deal.reasons || [];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -20 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="relative"
      data-testid={`ai-pick-${deal.business_id}`}
    >
      {/* Previous pick reminder */}
      <PreviousPickReminder reminder={previousReminder} onGoBack={onGoBackToPrevious} />

      {/* Pick badge */}
      <div className="flex justify-center mb-3">
        <span className="text-[10px] font-bold text-violet-500 bg-violet-50 border border-violet-200 px-3 py-1 rounded-full">
          AI Pick {pickNumber} of {totalPicks}
        </span>
      </div>

      <div
        className="rounded-2xl p-5 border-2 shadow-lg relative overflow-hidden"
        style={{ borderColor: brandColor, background: `linear-gradient(135deg, ${brandColor}08, ${brandColor}03)` }}
      >
        {/* Glow effect */}
        <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 50% 0%, ${brandColor}, transparent 70%)` }} />

        <div className="relative">
          {/* Deal info */}
          <div className="flex items-start gap-4 mb-4">
            <BrandLogo name={deal.name} logoUrl={deal.logo_url} brandColor={brandColor} />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-extrabold text-slate-900">{deal.name}</h3>
              <p className="text-sm text-slate-500 mt-0.5">{deal.offer_title}</p>
              <div className="flex items-center gap-3 mt-2">
                {deal.offer_value_est > 0 && (
                  <span className="text-lg font-extrabold text-emerald-600">${deal.offer_value_est}</span>
                )}
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i < (deal.ease_stars || 0) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Comparative reasoning — the WHY */}
          {comparativeReasons.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="ai-comparative-reasons">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                <span className="text-[11px] font-bold text-violet-600 uppercase tracking-wider">Why this one?</span>
              </div>
              {comparativeReasons.map((reason, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 * (i + 1) }}
                  className="flex items-start gap-2 text-sm text-slate-700"
                  data-testid={`ai-reason-${i}`}
                >
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>{reason}</span>
                </motion.div>
              ))}
            </div>
          )}

          {/* Quick trait pills (legacy) — only show if no comparative reasons */}
          {comparativeReasons.length === 0 && genericReasons.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {genericReasons.map((r, i) => (
                <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200">
                  {r}
                </span>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onAccept(deal)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold text-sm transition-all"
              style={{ background: brandColor }}
              data-testid="ai-accept-btn"
            >
              <Check className="w-4 h-4" /> Looks Good
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onReject(deal)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all"
              data-testid="ai-reject-btn"
            >
              Show Me Another
            </motion.button>
          </div>

          {/* Rejection reasons (shown after reject) */}
          <AnimatePresence>
            {showReasons && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <p className="text-[11px] text-slate-400 text-center mt-3 mb-2">Why not this one? (optional)</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {REJECTION_REASONS.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => onReasonSelect(r.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-[10px] font-semibold text-slate-500 hover:bg-slate-50 transition-colors"
                      data-testid={`rejection-reason-${r.id}`}
                    >
                      <r.icon className="w-3 h-3" /> {r.label}
                    </button>
                  ))}
                  <button
                    onClick={() => onReasonSelect("skip")}
                    className="px-2.5 py-1.5 rounded-lg text-[10px] font-semibold text-slate-400 hover:text-slate-500"
                  >
                    Skip
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

export default function DecisionEnginePage() {
  const navigate = useNavigate();
  const { getStatus } = useUnifiedStatus();
  const [phase, setPhase] = useState("select");
  const [favorites, setFavorites] = useState([]);
  const [selected, setSelected] = useState([]);
  const [ranked, setRanked] = useState([]);
  const [aiIndex, setAiIndex] = useState(0);
  const [showReasons, setShowReasons] = useState(false);
  const [rejections, setRejections] = useState([]);
  const [finalChoice, setFinalChoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommending, setRecommending] = useState(false);
  const [rejectedIds, setRejectedIds] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [priorSession, setPriorSession] = useState(null);
  const [priorBannerDismissed, setPriorBannerDismissed] = useState(false);
  const sessionId = useRef(crypto.randomUUID?.() || Date.now().toString());

  useEffect(() => {
    const loadData = async () => {
      try {
        // v1.0.0 Gate 4 Item 4 — load prior session context (persistent memory)
        const [rejRes, favRes, priorRes] = await Promise.all([
          axios.get(`${API}/decisions/rejected`),
          axios.get(`${API}/favorites`),
          axios.get(`${API}/decisions/last-session`).catch(() => ({ data: null })),
        ]);
        const persistedRejections = rejRes.data?.rejected_ids || [];
        setRejectedIds(persistedRejections);

        if (priorRes?.data?.has_prior_session) {
          setPriorSession(priorRes.data.session);
        }

        const favData = favRes.data || [];
        // Filter out persistently rejected businesses
        const filteredFavs = favData.filter(f => !persistedRejections.includes(f.business_id));
        if (!filteredFavs.length) { setFavorites([]); setLoading(false); return; }
        const ids = filteredFavs.map(f => f.business_id);
        const enriched = await axios.post(`${API}/decisions/recommend`, { business_ids: ids }).catch(() => null);
        if (enriched?.data?.ranked_deals) {
          setFavorites(enriched.data.ranked_deals.filter(d => !persistedRejections.includes(d.business_id)));
        } else {
          setFavorites(filteredFavs.map(f => ({
            business_id: f.business_id, name: f.business_name || "", category: f.category || "",
            offer_title: f.offer_title || "", offer_value_est: f.offer_value_est || 0,
            logo_url: f.logo_url || "", brand_colors: [], ease_stars: 3,
          })));
        }
      } catch {
        toast.error("Couldn't load favorites");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Prefill selected from prior session when user accepts the banner
  const acceptPriorSession = () => {
    if (!priorSession) return;
    const prevSelected = (priorSession.deals_presented || []).filter((id) =>
      favorites.some((f) => f.business_id === id)
    );
    if (prevSelected.length) {
      setSelected(prevSelected.slice(0, 5));
      toast.success("Picked up where you left off");
    }
    setPriorBannerDismissed(true);
  };

  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 5) { toast("Maximum 5 deals", { duration: 1500 }); return prev; }
      return [...prev, id];
    });
  }, []);

  const startManualChoose = () => {
    if (selected.length < 2) return toast.error("Select at least 2 deals");
    setPhase("choose");
  };

  const startAIAssist = async () => {
    if (selected.length < 2) return toast.error("Select at least 2 deals");
    setRecommending(true);
    try {
      const res = await axios.post(`${API}/decisions/recommend`, { business_ids: selected });
      setRanked(res.data.ranked_deals);
      setAiIndex(0);
      setRejections([]);
      setPhase("ai");
    } catch {
      toast.error("Failed to get recommendation");
    } finally {
      setRecommending(false);
    }
  };

  const handleManualPick = (deal) => {
    setFinalChoice(deal);
    setPhase("result");
    logDecision("manual", deal);
  };

  const handleAIAccept = (deal) => {
    setFinalChoice(deal);
    setPhase("result");
    logDecision("ai_assist", deal);
  };

  const handleAIReject = () => {
    setShowReasons(true);
  };

  const handleGoBackToPrevious = () => {
    if (aiIndex > 0) {
      setAiIndex(prev => prev - 1);
      setShowReasons(false);
      // Remove the last rejection
      setRejections(prev => prev.slice(0, -1));
    }
  };

  const handleReasonSelect = (reason) => {
    const currentDeal = ranked[aiIndex];
    const newRejections = [...rejections, { business_id: currentDeal.business_id, reason, order: rejections.length + 1 }];
    setRejections(newRejections);
    setShowReasons(false);

    // Persist rejection to backend
    axios.post(`${API}/decisions/reject/${currentDeal.business_id}`, {
      reason,
      session_id: sessionId.current,
    }).catch(() => {});
    setRejectedIds(prev => [...prev, currentDeal.business_id]);

    // After 2 rejections, fall back to manual
    if (newRejections.length >= 2 && aiIndex + 1 < ranked.length - 1) {
      toast("Here are the remaining deals — your call.", { duration: 3000 });
      setPhase("choose");
      return;
    }

    // Move to next pick
    if (aiIndex + 1 < ranked.length) {
      setAiIndex(prev => prev + 1);
    } else {
      toast("That was the last option. Pick from the list?", { duration: 3000 });
      setPhase("choose");
    }
  };

  const handleAction = async (action) => {
    if (!finalChoice) return;
    try {
      if (action === "favorite") {
        await axios.post(`${API}/favorites/${finalChoice.business_id}`);
        toast.success(`${finalChoice.name} saved to favorites!`);
      } else if (action === "itinerary") {
        navigate(`/my-itinerary?add=${finalChoice.business_id}`);
        return;
      }
    } catch {
      toast.error("Action failed");
    }
  };

  const logDecision = async (mode, deal) => {
    try {
      await axios.post(`${API}/decisions/log`, {
        session_id: sessionId.current,
        category: deal?.category || "",
        mode,
        deals_presented: selected,
        ai_picks_order: ranked.map(r => r.business_id),
        rejections,
        final_choice: deal?.business_id || null,
        action_taken: "",
        resulted_in_save: false,
      });
    } catch {}
  };

  const reset = () => {
    setPhase("select");
    setSelected([]);
    setRanked([]);
    setAiIndex(0);
    setRejections([]);
    setFinalChoice(null);
    setShowReasons(false);
    sessionId.current = crypto.randomUUID?.() || Date.now().toString();
  };

  const resetAllRejections = async () => {
    try {
      await axios.delete(`${API}/decisions/rejected`);
      setRejectedIds([]);
      toast.success("All rejections cleared! Refreshing...");
      setShowSettings(false);
      setLoading(true);
      // Reload all favorites fresh
      const favRes = await axios.get(`${API}/favorites`);
      const favData = favRes.data || [];
      const ids = favData.map(f => f.business_id);
      const enriched = await axios.post(`${API}/decisions/recommend`, { business_ids: ids }).catch(() => null);
      if (enriched?.data?.ranked_deals) {
        setFavorites(enriched.data.ranked_deals);
      } else {
        setFavorites(favData.map(f => ({
          business_id: f.business_id, name: f.business_name || "", category: f.category || "",
          offer_title: f.offer_title || "", offer_value_est: f.offer_value_est || 0,
          logo_url: f.logo_url || "", brand_colors: [], ease_stars: 3,
        })));
      }
      reset();
    } catch {
      toast.error("Failed to reset");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  const chooseDeals = phase === "choose"
    ? favorites.filter(f => selected.includes(f.business_id) && !rejections.find(r => r.business_id === f.business_id))
    : [];

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="decision-engine-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-500 to-indigo-600 text-white px-4 pt-12 pb-6">
        <button onClick={() => navigate(-1)} className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/15 flex items-center justify-center" data-testid="decision-back-btn">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-amber-300" />
          <h1 className="text-2xl font-extrabold tracking-tight">My Decision</h1>
          <p className="text-violet-200 text-sm mt-1">
            {phase === "select" && "Pick up to 5 deals you're torn between"}
            {phase === "choose" && "Tap the one you want"}
            {phase === "ai" && "Let us help you decide"}
            {phase === "result" && "Great choice!"}
          </p>
        </div>
      </div>

      <div className="px-4 pt-4">
        {/* PHASE 1: SELECT FROM FAVORITES */}
        {phase === "select" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {/* v1.0.0 Gate 4 Item 4 — Prior session banner */}
            {priorSession && !priorBannerDismissed && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-violet-200 bg-violet-50 p-3 flex items-center gap-3"
                data-testid="decision-prior-session-banner"
              >
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-violet-700">Pick up where you left off?</p>
                  <p className="text-[11px] text-violet-600 truncate">
                    {priorSession.deals_presented?.length || 0} deals from your last session
                  </p>
                </div>
                <button
                  onClick={acceptPriorSession}
                  className="px-3 py-1.5 rounded-lg bg-violet-500 text-white text-[11px] font-bold"
                  data-testid="decision-prior-accept-btn"
                >
                  Resume
                </button>
                <button
                  onClick={() => setPriorBannerDismissed(true)}
                  className="p-1 text-violet-400"
                  data-testid="decision-prior-dismiss-btn"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}
            {selected.length > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">{selected.length} of 5 selected</span>
                <div className="flex gap-1.5">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className={`w-2 h-2 rounded-full ${i < selected.length ? "bg-violet-500" : "bg-slate-200"}`} />
                  ))}
                </div>
              </div>
            )}

            {favorites.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 font-medium">No favorites yet</p>
                <p className="text-xs text-slate-400 mt-1">Save some deals first, then come back to decide</p>
                <button onClick={() => navigate("/")} className="mt-4 px-4 py-2 rounded-xl bg-violet-500 text-white text-sm font-bold" data-testid="decision-browse-btn">
                  Browse Deals
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-2" data-testid="decision-favorites-list">
                  {favorites.map(deal => (
                    <DealSelectionCard
                      key={deal.business_id}
                      deal={deal}
                      selected={selected.includes(deal.business_id)}
                      onToggle={toggleSelect}
                      disabled={selected.length >= 5 && !selected.includes(deal.business_id)}
                      dealStatus={getStatus(deal.business_id)}
                    />
                  ))}
                </div>

                {selected.length >= 2 && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 flex gap-2 z-50" data-testid="decision-actions-bar">
                    <button
                      onClick={startManualChoose}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-violet-500 text-violet-600 font-bold text-sm"
                      data-testid="decision-manual-btn"
                    >
                      I'll Pick
                    </button>
                    <button
                      onClick={startAIAssist}
                      disabled={recommending}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-violet-500 text-white font-bold text-sm disabled:opacity-50"
                      data-testid="decision-ai-btn"
                    >
                      {recommending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Pick for Me
                    </button>
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* PHASE 2: MANUAL CHOOSE */}
        {phase === "choose" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <p className="text-sm text-slate-500 text-center mb-2">Tap the deal you want</p>
            <div className="space-y-2" data-testid="decision-choose-list">
              {chooseDeals.map(deal => {
                const brandColor = deal.brand_colors?.[0] || "#3b82f6";
                return (
                  <motion.button
                    key={deal.business_id}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleManualPick(deal)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-violet-400 hover:shadow-md transition-all text-left"
                    data-testid={`decision-pick-${deal.business_id}`}
                  >
                    <BrandLogo name={deal.name} logoUrl={deal.logo_url} brandColor={brandColor} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate">{deal.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{deal.offer_title || deal.category}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {deal.offer_value_est > 0 && <p className="text-base font-extrabold text-emerald-600">${deal.offer_value_est}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  </motion.button>
                );
              })}
            </div>
            <button onClick={() => setPhase("select")} className="w-full text-center text-xs text-slate-400 py-2" data-testid="decision-back-to-select">
              Back to selection
            </button>
          </motion.div>
        )}

        {/* PHASE 3: AI RECOMMENDATION */}
        {phase === "ai" && ranked[aiIndex] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <AnimatePresence mode="wait">
              <AIRecommendationCard
                key={ranked[aiIndex].business_id}
                deal={ranked[aiIndex]}
                onAccept={handleAIAccept}
                onReject={handleAIReject}
                showReasons={showReasons}
                onReasonSelect={handleReasonSelect}
                pickNumber={aiIndex + 1}
                totalPicks={ranked.length}
                previousReminder={ranked[aiIndex].previous_pick_reminder}
                onGoBackToPrevious={handleGoBackToPrevious}
              />
            </AnimatePresence>

            <button onClick={() => { setPhase("choose"); }} className="w-full text-center text-xs text-slate-400 py-2" data-testid="decision-pick-myself">
              Let me pick myself
            </button>
          </motion.div>
        )}

        {/* PHASE 4: RESULT */}
        {phase === "result" && finalChoice && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4" data-testid="decision-result">
            <div className="text-center py-2">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}>
                <Check className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              </motion.div>
              <h2 className="text-xl font-extrabold text-slate-800">Great choice!</h2>
              <p className="text-sm text-slate-400 mt-1">What would you like to do with it?</p>
            </div>

            {/* Selected deal card */}
            <div className="rounded-2xl bg-white border-2 border-emerald-200 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <BrandLogo name={finalChoice.name} logoUrl={finalChoice.logo_url} brandColor={finalChoice.brand_colors?.[0] || "#10b981"} />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-extrabold text-slate-900">{finalChoice.name}</h3>
                  <p className="text-sm text-slate-500">{finalChoice.offer_title}</p>
                  {finalChoice.offer_value_est > 0 && (
                    <span className="text-lg font-extrabold text-emerald-600 mt-1 block">${finalChoice.offer_value_est}</span>
                  )}
                </div>
              </div>
              {/* Show reasons why this was a good pick */}
              {finalChoice.comparative_reasons?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-100 space-y-1.5" data-testid="result-reasons">
                  {finalChoice.comparative_reasons.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 text-[12px] text-emerald-700">
                      <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              <button
                onClick={() => handleAction("itinerary")}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-violet-500 text-white font-bold text-sm"
                data-testid="decision-add-itinerary"
              >
                <CalendarPlus className="w-4 h-4" /> Add to Itinerary
              </button>
              <button
                onClick={() => handleAction("favorite")}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 border-violet-300 text-violet-600 font-bold text-sm"
                data-testid="decision-save-favorite"
              >
                <Heart className="w-4 h-4" /> Keep in Favorites
              </button>
              <button
                onClick={reset}
                className="w-full text-center text-xs text-slate-400 py-2"
                data-testid="decision-start-over"
              >
                Start over
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
